import { create } from 'zustand'

import { findIndex, trim } from 'lodash'
import { createMeet, getMeet, removeMeet, stopRecordingMeet, updateMeet } from '../api/meet'
import { getMemoDetail, getMemoList, getMemoPrompt, getMemoShare, putMemo } from '../api/memo'
import {
    archiveProject,
    createProject,
    deleteProject,
    getProject,
    getProjectDetail,
    patchProject,
} from '../api/project'
import {
    archiveRecord,
    createPresignedUrl,
    createRecord,
    createStateByRecord,
    deleteRecord,
    getExternalFiles,
    getRecordDetail,
    getRecordList,
    patchRecord,
    uploadFile,
} from '../api/record'
import { getStenographyDetail, getStenographyList, getStenographyShare } from '../api/stenography'
import {
    BodyWorkplaceUpdateApiV1AdjutantAppWorkplaceWorkplaceIdPatch,
    MeetingCreateModel,
    MeetingDTO,
    MeetingDeleteApiV1AdjutantAppMeetingMeetingIdDeleteParams,
    MeetingDeleteOutputDTO,
    MeetingListApiV1AdjutantAppMeetingGetParams,
    MeetingStopRecordingApiV1AdjutantAppMeetingMeetingIdStopRecordingPostParams,
    MeetingUpdateApiV1AdjutantAppMeetingMeetingIdPutParams,
    MeetingUpdateModel,
    MemoDTO,
    MemoDetailApiV1AdjutantAppMemoMemoIdGetParams,
    MemoDetailShareApiV1AdjutantAppMemoMemoIdShareGetParams,
    MemoListApiV1CollaborationAppMemoGetParams,
    MemoListOutputDTO,
    MemoPromptListApiV1AdjutantAppMemoPromptGetParams,
    MemoPromptListOutputDTO,
    MemoPublicDTO,
    MemoUpdateApiV1AdjutantAppMemoMemoIdPutParams,
    MemoUpdateModel,
    RMRModel,
    RecordArchiveApiV1AdjutantAppRecordRecordIdArchivePostParams,
    RecordArchiveModel,
    RecordCreateModel,
    RecordDTO,
    RecordDeleteApiV1AdjutantAppRecordRecordIdDeleteParams,
    RecordDeleteOutputDTO,
    RecordDetailApiV1AdjutantAppRecordRecordIdGetParams,
    RecordListApiV1AdjutantAppRecordGetParams,
    RecordListOutputDTO,
    RecordStenographyDTO,
    RecordStenographyDetailApiV1AdjutantAppRecordStenographyRecordStenographyIdGetParams,
    RecordStenographyDetailApiV1AdjutantAppRecordStenographyRecordStenographyIdShareGetParams,
    RecordStenographyListApiV1AdjutantAppRecordStenographyGetParams,
    RecordStenographyListOutputDTO,
    RecordStenographyPublicDTO,
    RecordStenographyShortDTO,
    RecordUpdateApiV1AdjutantAppRecordRecordIdPatchParams,
    RecordUpdateModel,
    WorkplaceArchiveApiV1AdjutantAppWorkplaceWorkplaceIdArchivePostParams,
    WorkplaceArchiveModel,
    WorkplaceCreateModel,
    WorkplaceDTO,
    WorkplaceDeleteApiV1AdjutantAppWorkplaceWorkplaceIdDeleteParams,
    WorkplaceDeleteOutputDTO,
    WorkplaceDetailApiV1AdjutantAppWorkplaceWorkplaceIdGetParams,
    WorkplaceListApiV1AdjutantAppWorkplaceGetParams,
    WorkplaceListOutputDTO,
    WorkplaceUpdateApiV1AdjutantAppWorkplaceWorkplaceIdPatchParams,
} from '../swagger/dto'
import { ExternalUploadDTO, FileLifeType } from '../swagger/dtoGateway'

interface AssistantAdministrationState {
    workplace?: WorkplaceDTO[]
    workplaceAll?: WorkplaceDTO[]
    records: RecordListOutputDTO
    record?: RecordDTO

    stenographyList?: RecordStenographyShortDTO[]
    stenographyDetail?: RecordStenographyDTO
    stenographyPublic?: RecordStenographyPublicDTO

    memoDetail?: MemoDTO
    memoPublic?: MemoPublicDTO

    fetchWorkplace: (
        { params, isAll }: WorkplaceListApiV1AdjutantAppWorkplaceGetParams,
        { isAll: boolean },
    ) => Promise<WorkplaceListOutputDTO>
    errorFetchWorkplace?: string
    isLoadingFetchWorkplace: boolean

    workplaceDetail?: WorkplaceDTO
    fetchWorkplaceDetail: ({
        params,
    }: WorkplaceDetailApiV1AdjutantAppWorkplaceWorkplaceIdGetParams) => Promise<WorkplaceDTO>
    errorFetchWorkplaceDetail?: string
    isLoadingFetchWorkplaceDetail: boolean

    createWorkplace: ({ data }: WorkplaceCreateModel) => Promise<WorkplaceDTO>
    errorCreateWorkplace?: string
    isLoadingCreateWorkplace: boolean

    createMeeting: ({ data }: MeetingCreateModel) => Promise<MeetingDTO>
    errorCreateMeeting?: string
    isLoadingCreateMeeting: boolean
    isCreateMeetingDone: boolean

    updateMeeting: (
        params: MeetingUpdateApiV1AdjutantAppMeetingMeetingIdPutParams,
        payload: MeetingUpdateModel,
    ) => Promise<MeetingDTO>
    errorUpdateMeeting?: string
    isLoadingUpdateMeeting: boolean

    stopRecordingMeeting: ({
        params,
    }: MeetingStopRecordingApiV1AdjutantAppMeetingMeetingIdStopRecordingPostParams) => Promise<MeetingDTO>
    errorStopRecordingMeeting?: string
    isLoadingStopRecordingMeeting: boolean

    removeRecordingMeeting: ({
        params,
    }: MeetingDeleteApiV1AdjutantAppMeetingMeetingIdDeleteParams) => Promise<MeetingDeleteOutputDTO>
    errorRemoveMeeting?: string
    isLoadingRemoveMeeting: boolean

    createStateByRecord: ({ data }: RMRModel) => Promise<RecordDTO>
    errorCreateStateByRecord?: string
    isLoadingCreateStateByRecord: boolean

    updateWorkplace: (
        params: WorkplaceUpdateApiV1AdjutantAppWorkplaceWorkplaceIdPatchParams,
        payload: BodyWorkplaceUpdateApiV1AdjutantAppWorkplaceWorkplaceIdPatch,
    ) => Promise<void>
    errorUpdateWorkplace?: string
    isLoadingUpdateWorkplace: boolean

    tabRecordDetail: string

    setRecord: (record: string) => void
    fetchRecord: ({ params }: RecordListApiV1AdjutantAppRecordGetParams) => Promise<RecordDTO[]>
    errorFetchRecord?: string
    isLoadingFetchRecord: boolean

    fetchRecordRefrech: ({
        params,
    }: RecordListApiV1AdjutantAppRecordGetParams) => Promise<RecordDTO[]>

    meeting?: MeetingDTO[]
    fetchMeeting: ({ params }: MeetingListApiV1AdjutantAppMeetingGetParams) => Promise<MeetingDTO[]>
    errorFetchMeeting?: string
    isLoadingFetchMeeting: boolean

    fetchRecordDetail: ({
        params,
    }: RecordDetailApiV1AdjutantAppRecordRecordIdGetParams) => Promise<RecordDTO>
    errorFetchRecordDetail?: string
    isLoadingFetchRecordDetail: boolean

    updateRecord: (
        params: RecordUpdateApiV1AdjutantAppRecordRecordIdPatchParams,
        payload: RecordUpdateModel,
    ) => Promise<RecordDTO>
    errorUpdateRecord?: string
    isLoadingUpdateRecord: boolean

    createRecord: ({ params }: RecordCreateModel) => Promise<RecordDTO>
    isLoadingFetchRecord: true
    errorCreateRecord: undefined
    clearCreateRecordError: () => void
    uploadFile: ({
        params,
    }: {
        title?: string
        extra_context?: string
        file: File
        memo_prompt_id: string
        workplaceId?: string
    }) => Promise<void>
    errorUploadFile?: string
    isLoadingUploadFile: boolean
    clearUploadFileError: () => void
    errorFetchMemoList?: boolean
    fetchMemoList: (
        params: MemoListApiV1CollaborationAppMemoGetParams,
    ) => Promise<MemoListOutputDTO>

    fetchStenographyList: (
        params: RecordStenographyListApiV1AdjutantAppRecordStenographyGetParams,
    ) => Promise<RecordStenographyListOutputDTO>
    errorFetchStenographyList?: string
    isLoadingFetchStenographyList: boolean

    fetchStenographyDetail: (
        params: RecordStenographyDetailApiV1AdjutantAppRecordStenographyRecordStenographyIdGetParams,
    ) => Promise<RecordStenographyDTO>
    errorFetchStenographyDetail?: string
    isLoadingFetchStenographyDetail: boolean

    fetchStenographyShare: (
        params: RecordStenographyDetailApiV1AdjutantAppRecordStenographyRecordStenographyIdShareGetParams,
    ) => Promise<RecordStenographyPublicDTO>
    errorFetchStenographyShare?: string
    isLoadingFetchStenographyShare: boolean

    memoPrompt?: MemoPromptListOutputDTO
    fetchMemoPrompt: (
        params: MemoPromptListApiV1AdjutantAppMemoPromptGetParams,
    ) => Promise<MemoPromptListOutputDTO>
    errorFetchMemoPrompt?: string
    isLoadingFetchMemoPrompt: boolean

    memoDetail: undefined
    fetchMemoDetail: (params: MemoDetailApiV1AdjutantAppMemoMemoIdGetParams) => Promise<MemoDTO>
    errorFetchMemoDetail?: string
    isLoadingFetchMemoDetail: boolean

    fetchMemoShare: (
        params: MemoDetailShareApiV1AdjutantAppMemoMemoIdShareGetParams,
    ) => Promise<MemoPublicDTO>
    errorFetchMemoShare?: string
    isLoadingFetchMemoShare: boolean

    updateMemo: (
        params: MemoUpdateApiV1AdjutantAppMemoMemoIdPutParams,
        payload: MemoUpdateModel,
    ) => Promise<MemoUpdateModel>
    errorUpdateMemo?: string
    isLoadingUpdateMemo: boolean

    externalFiles?: ExternalUploadDTO[]
    fetchExternalFiles: () => Promise<ExternalUploadDTO[]>
    errorFetchExternalFiles?: string
    isLoadingFetchExternalFiles: boolean

    deleteRecord: (
        params: RecordDeleteApiV1AdjutantAppRecordRecordIdDeleteParams,
    ) => Promise<RecordDeleteOutputDTO>
    isLoadingDeleteRecord: boolean
    errorDeleteRecord?: boolean

    archiveRecord: (
        params: RecordArchiveApiV1AdjutantAppRecordRecordIdArchivePostParams,
        data: RecordArchiveModel,
    ) => Promise<RecordDTO>
    isLoadingArchiveRecord: boolean
    errorArchiveRecord?: boolean

    deleteWorkplace: (
        params: WorkplaceDeleteApiV1AdjutantAppWorkplaceWorkplaceIdDeleteParams,
    ) => Promise<WorkplaceDeleteOutputDTO>
    isLoadingDeleteWorkplace: boolean
    errorDeleteWorkplace?: boolean

    archiveWorkplace: (
        params: WorkplaceArchiveApiV1AdjutantAppWorkplaceWorkplaceIdArchivePostParams,
        data: WorkplaceArchiveModel,
    ) => Promise<WorkplaceDTO>
    isLoadingArchiveWorkplace: boolean
    errorArchiveWorkplace?: boolean

    idActiveEditRecord?: string
    setIdActiveEditRecord: (id?: string) => void
    resetCreateMeetingError: VoidFunction
}

export const useAssistantAdministrationStore = create<AssistantAdministrationState>((set, get) => ({
    workplace: undefined,
    records: {
        limit: 0,
        objects: [],
        offset: 0,
        total_count: 0,
    },
    record: undefined,

    errorFetchWorkplace: undefined,
    isLoadingFetchWorkplace: false,
    fetchWorkplace: async (params, isAll) => {
        try {
            set({
                isLoadingFetchWorkplace: true,
                errorFetchWorkplace: undefined,
            })
            const data = await getProject(isAll ? { limit: 100 } : params)
            if (isAll) {
                set({
                    isLoadingFetchWorkplace: false,
                    workplaceAll: data.objects,
                })
            } else {
                if (params.is_archived) {
                    set({
                        isLoadingFetchWorkplace: false,
                        workplaceArchived: data.objects,
                    })
                } else {
                    set({
                        isLoadingFetchWorkplace: false,
                        workplace: data.objects,
                    })
                }
            }
        } catch (error) {
            set({
                isLoadingFetchWorkplace: false,
                errorFetchWorkplace: 'Ошибка',
            })
        }
    },

    workplaceDetail: undefined,
    errorFetchWorkplaceDetail: undefined,
    isLoadingFetchWorkplaceDetail: false,
    fetchWorkplaceDetail: async (params) => {
        try {
            set({
                workplaceDetail: undefined,
                isLoadingFetchWorkplaceDetail: true,
                errorFetchWorkplaceDetail: undefined,
            })
            const data = await getProjectDetail(params)
            set({
                isLoadingFetchWorkplaceDetail: false,
                workplaceDetail: data,
            })
        } catch (error) {
            set({
                isLoadingFetchWorkplaceDetail: false,
                errorFetchWorkplaceDetail: 'Ошибка',
            })
        }
    },

    errorStopRecordingMeeting: undefined,
    isLoadingStopRecordingMeeting: false,
    stopRecordingMeeting: async (data) => {
        try {
            set({
                isLoadingStopRecordingMeeting: true,
                errorStopRecordingMeeting: undefined,
            })
            await stopRecordingMeet(data)

            set({
                isLoadingStopRecordingMeeting: false,
            })
        } catch (error) {
            set({
                isLoadingStopRecordingMeeting: false,
                errorStopRecordingMeeting: 'Ошибка',
            })
        }
    },

    errorRemoveMeeting: undefined,
    isLoadingRemoveMeeting: false,
    removeRecordingMeeting: async (data) => {
        try {
            set({
                isLoadingRemoveMeeting: true,
                errorRemoveMeeting: undefined,
            })
            await removeMeet(data)
            set({
                isLoadingRemoveMeeting: false,
            })
        } catch (error) {
            set({
                isLoadingRemoveMeeting: false,
                errorRemoveMeeting: 'Ошибка',
            })
        }
    },

    isUpdateMeetingDone: false,
    errorUpdateMeeting: undefined,
    isLoadingUpdateMeeting: false,
    updateMeeting: async (params, payload) => {
        try {
            set({
                isUpdateMeetingDone: false,
                isLoadingUpdateMeeting: true,
                errorUpdateMeeting: undefined,
            })
            await updateMeet(params, payload)
            set({
                isUpdateMeetingDone: true,
                isLoadingUpdateMeeting: false,
            })
        } catch (error) {
            set({
                isLoadingUpdateMeeting: false,
                errorUpdateMeeting: 'Ошибка',
            })
        }
    },

    isCreateMeetingDone: false,
    errorCreateMeeting: undefined,
    isLoadingCreateMeeting: false,
    createMeeting: async (data) => {
        try {
            set({
                isCreateMeetingDone: false,
                isLoadingCreateMeeting: true,
                errorCreateMeeting: undefined,
            })
            await createMeet(data)
            set({
                isCreateMeetingDone: true,
            })
        } catch (error) {
            set({
                isLoadingCreateMeeting: false,
                errorCreateMeeting:
                    error?.response?.data?.detail?.error_code === 'NoBalanceForOperation'
                        ? 'Недостаточно средств для проведения операции, требуется пополнить баланс'
                        : 'Ошибка подключения бота к встрече',
            })
        }
    },
    resetCreateMeetingError: () => {
        set({
            isCreateMeetingDone: false,
            isLoadingCreateMeeting: false,
            errorCreateMeeting: undefined,
        })
    },

    errorCreateWorkplace: undefined,
    isLoadingCreateWorkplace: false,
    createWorkplace: async (data) => {
        try {
            set({
                isLoadingCreateWorkplace: true,
                errorCreateWorkplace: undefined,
            })
            const workplace = await createProject(data)
            const workplaceList = await get().workplace
            set({
                isLoadingCreateWorkplace: false,
                workplace: workplaceList?.length ? [workplace, ...workplaceList] : [workplace],
            })
        } catch (error) {
            set({
                isLoadingCreateWorkplace: false,
                errorCreateWorkplace: 'Ошибка',
            })
        }
    },

    errorFetchStenographyList: undefined,
    isLoadingFetchStenographyList: false,
    fetchStenographyList: async (params) => {
        try {
            set({
                stenographyDetail: undefined,
                stenographyList: undefined,
                isLoadingFetchStenographyList: true,
                errorFetchStenographyList: undefined,
            })
            const data = await getStenographyList(params)
            set({
                isLoadingFetchStenographyList: false,
                stenographyList: data.objects,
            })
        } catch (error) {
            set({
                isLoadingFetchStenographyList: false,
                errorFetchStenographyList: 'Ошибка',
            })
        }
    },

    errorFetchStenographyDetail: undefined,
    isLoadingFetchStenographyDetail: false,
    fetchStenographyDetail: async (params) => {
        try {
            set({
                stenographyDetail: undefined,
                isLoadingFetchStenographyDetail: true,
                errorFetchStenographyDetail: undefined,
            })

            const data = await getStenographyDetail(params)
            set({
                isLoadingFetchStenographyDetail: false,
                stenographyDetail: data,
            })
        } catch (error) {
            set({
                isLoadingFetchStenographyDetail: false,
                errorFetchStenographyDetail: 'Ошибка',
            })
        }
    },

    memoPrompt: undefined,
    errorFetchMemoPrompt: undefined,
    isLoadingFetchMemoPrompt: false,
    fetchMemoPrompt: async (params) => {
        try {
            set({
                memoPrompt: undefined,
                isLoadingFetchMemoPrompt: true,
                errorFetchMemoPrompt: undefined,
                errorCreateRecord: undefined,
                errorUploadFile: undefined,
            })
            const data = await getMemoPrompt(params)
            set({
                isLoadingFetchMemoPrompt: false,
                memoPrompt: data.objects,
            })
        } catch (error) {
            set({
                isLoadingFetchMemoPrompt: false,
                errorFetchMemoPrompt: 'Ошибка',
            })
        }
    },

    errorFetchMemoDetail: undefined,
    isLoadingFetchMemoDetail: false,
    fetchMemoDetail: async (params) => {
        try {
            set({
                memoDetail: undefined,
                isLoadingFetchMemoDetail: true,
                errorFetchMemoDetail: undefined,
            })
            const data = await getMemoDetail(params)
            const memo = {
                ...data,
                content: trim(trim(trim(data.content, '---'), '```'), 'markdown'),
            }

            set({
                isLoadingFetchMemoDetail: false,
                memoDetail: memo,
            })
        } catch (error) {
            set({
                isLoadingFetchMemoDetail: false,
                errorFetchMemoDetail: 'Ошибка',
            })
        }
    },

    memoList: undefined,
    errorFetchMemoList: undefined,
    isLoadingFetchMemoList: false,
    fetchMemoList: async (params) => {
        try {
            set({
                // memoDetail: undefined,
                memoList: undefined,
                isLoadingFetchMemoList: true,
                errorFetchMemoList: undefined,
            })
            const data = await getMemoList(params)

            set({
                isLoadingFetchMemoList: false,
                memoList: data.objects,
            })
        } catch (error) {
            set({
                isLoadingFetchMemoDetail: false,
                errorFetchMemoList: 'Ошибка',
            })
        }
    },

    errorFetchStenographyShare: undefined,
    isLoadingFetchStenographyShare: false,
    fetchStenographyShare: async (params) => {
        try {
            set({
                isLoadingFetchStenographyShare: true,
                errorFetchStenographyShare: undefined,
            })
            const data = await getStenographyShare(params)
            set({
                isLoadingFetchStenographyShare: false,
                stenographyPublic: data,
            })
        } catch (error) {
            set({
                isLoadingFetchStenographyShare: false,
                errorFetchStenographyShare: 'Ошибка',
            })
        }
    },

    errorFetchMemoShare: undefined,
    isLoadingFetchMemoShare: false,
    fetchMemoShare: async (params) => {
        try {
            set({
                isLoadingFetchMemoShare: true,
                errorFetchMemoShare: undefined,
            })
            const data = await getMemoShare(params)
            set({
                isLoadingFetchMemoShare: false,
                memoPublic: data,
            })
        } catch (error) {
            set({
                isLoadingFetchMemoShare: false,
                errorFetchMemoShare: 'Ошибка',
            })
        }
    },

    errorUpdateRecord: undefined,
    isLoadingUpdateRecord: false,
    updateRecord: async (params, payload) => {
        try {
            set({
                isLoadingUpdateRecord: true,
                errorUpdateRecord: undefined,
            })
            const data = await patchRecord(params, payload)
            const recordsListBuffer = get().records ?? []
            const recordsIndexEdit = findIndex(recordsListBuffer.objects, { id: params.recordId })
            recordsListBuffer.objects[recordsIndexEdit] = data

            set({
                records: recordsListBuffer,
                isLoadingUpdateRecord: false,
                record: data,
            })
        } catch (error) {
            set({
                isLoadingUpdateRecord: false,
                errorUpdateRecord: 'Ошибка',
            })
        }
    },

    errorUpdateMemo: undefined,
    isLoadingUpdateMemo: false,
    updateMemo: async (params, payload) => {
        try {
            set({
                isLoadingUpdateMemo: true,
                errorUpdateMemo: undefined,
            })
            const data = await putMemo(params, payload)
            set({
                isLoadingUpdateMemo: false,
                memoDetail: data,
                memoPublic: data,
            })
        } catch (error) {
            set({
                isLoadingUpdateMemo: false,
                errorUpdateMemo: 'Ошибка',
            })
        }
    },

    errorUpdateWorkplace: undefined,
    isLoadingUpdateWorkplace: false,
    updateWorkplace: async (params, payload) => {
        try {
            set({
                isLoadingUpdateWorkplace: true,
                errorUpdateWorkplace: undefined,
            })
            const data = await patchProject(params, payload)
            set({
                isLoadingUpdateWorkplace: false,
                workplaceDetail: data,
            })
        } catch (error) {
            set({
                isLoadingUpdateWorkplace: false,
                errorUpdateWorkplace: 'Ошибка',
            })
        }
    },

    errorFetchRecord: undefined,
    isLoadingFetchRecord: false,
    fetchRecord: async (params) => {
        try {
            set({
                idActiveMemoDetail: undefined,
                records: undefined,
                isLoadingFetchRecord: true,
                errorFetchRecord: undefined,
            })
            const data = await getRecordList({ ...params })
            set({
                isLoadingFetchRecord: false,
                records: data,
            })
        } catch (error) {
            set({
                isLoadingFetchRecord: false,
                errorFetchRecord: 'Ошибка',
            })
        }
    },

    errorFetchMeeting: undefined,
    isLoadingFetchMeeting: false,
    fetchMeeting: async (params) => {
        try {
            set({
                isUpdateMeetingDone: false,
                meeting: undefined,
                isLoadingFetchMeeting: true,
                errorFetchMeeting: undefined,
            })
            const data = await getMeet({ ...params, limit: 100, desc: true })
            set({
                isLoadingFetchMeeting: false,
                meeting: data.objects,
            })
        } catch (error) {
            set({
                isLoadingFetchMeeting: false,
                errorFetchMeeting: 'Ошибка',
            })
        }
    },

    fetchRecordRefrech: async (params) => {
        try {
            const data = await getRecordList({ ...params })
            set({
                records: data,
            })
        } catch (error) {
            set({
                errorFetchRecord: 'Ошибка',
            })
        }
    },

    isGetFetchRecordRefresh: false,
    setIsGetFetchRecordRefresh: (value) => {
        set({
            setIsGetFetchRecordRefresh: value,
        })
    },

    isSuccessfulCreateRecord: false,
    setIsSuccessfulCreateRecord: (value) => {
        set({
            isSuccessfulCreateRecord: value,
        })
    },

    setRecord: (records) => {
        set({
            records,
        })
    },

    tabRecordDetail: 'detail',
    setTabRecordDetail: (tab) => {
        set({
            tabRecordDetail: tab,
        })
    },

    idActiveMemoDetail: undefined,
    setIdActiveMemoDetail: (id) => {
        set({
            idActiveMemoDetail: id,
        })
    },

    errorFetchRecordDetail: undefined,
    isLoadingFetchRecordDetail: false,
    fetchRecordDetail: async (params) => {
        try {
            set({
                idActiveMemoDetail: undefined,
                stenographyDetail: undefined,
                memoDetail: undefined,
                stenographyList: undefined,
                memoList: undefined,
                record: undefined,
                tabRecordDetail: 'detail',
                isLoadingFetchRecordDetail: true,
                errorFetchRecordDetail: undefined,
            })
            const data = await getRecordDetail(params)
            set({
                isLoadingFetchRecordDetail: false,
                record: data,
            })
        } catch (error) {
            set({
                isLoadingFetchRecordDetail: false,
                errorFetchRecordDetail: 'Ошибка',
            })
        }
    },

    isLoadingCreateRecord: false,
    errorCreateRecord: undefined,
    createRecord: async (params) => {
        try {
            set({
                isSuccessfulCreateRecord: false,
                isLoadingCreateRecord: true,
                errorCreateRecord: undefined,
                errorUploadFile: undefined,
            })
            await createRecord(params)
            set({
                isSuccessfulCreateRecord: true,
                isLoadingCreateRecord: false,
            })
        } catch (error) {
            set({
                isLoadingCreateRecord: false,
                errorCreateRecord:
                    error?.response?.data?.detail?.error_code === 'NoBalanceForOperation'
                        ? 'Недостаточно средств для проведения операции, требуется пополнить баланс'
                        : 'Возникла ошибка',
            })
        }
    },
    clearCreateRecordError: () => set({ errorCreateStateByRecord: undefined }),
    isLoadingCreateStateByRecord: true,
    errorCreateStateByRecord: undefined,
    createStateByRecord: async (data) => {
        try {
            set({
                isLoadingCreateStateByRecord: true,
                errorCreateStateByRecord: undefined,
            })
            const recordNew = await createStateByRecord(data)

            set({
                record: recordNew,
                isLoadingCreateStateByRecord: false,
            })
        } catch (error) {
            set({
                isLoadingCreateStateByRecord: false,
                errorCreateStateByRecord: 'Ошибка',
            })
        }
    },
    errorUploadFile: undefined,
    isLoadingUploadFile: false,
    uploadFile: async (params) => {
        try {
            const { file, workplaceId, memo_prompt_id, title, extra_context } = params

            set({
                isSuccessfulCreateRecord: false,
                isLoadingUploadFile: true,
                errorUploadFile: undefined,
                errorCreateRecord: undefined,
            })
            const name = file.name.replace(/[<>@#*+^,${}=|[\]\\]/g, '')
            const response = await createPresignedUrl({
                filename: name,
                life_type: FileLifeType.PERMANENT,
            })

            const formData = new FormData()
            Object.entries(response.fields).forEach(([key, value]) => {
                formData.append(key, value)
            })
            formData.append('file', file)

            await uploadFile({ url: response.url, formData })

            const newRecord = await createRecord({
                title,
                extra_context,
                source: response.future_url,
                workplace_id: workplaceId,
                memo_prompt_id,
            })
            const records = {
                ...get().records,
                objects: [...get().records.objects, newRecord],
            }

            set({
                isSuccessfulCreateRecord: true,
                isLoadingUploadFile: false,
                records,
            })
        } catch (error) {
            set({
                isLoadingUploadFile: false,
                errorUploadFile:
                    error?.response?.data?.detail?.error_code === 'NoBalanceForOperation'
                        ? 'Недостаточно средств для проведения операции, требуется пополнить баланс'
                        : 'Возникла ошибка',
            })
        }
    },
    clearUploadFileError: () => set({ errorUploadFile: undefined }),
    fetchExternalFiles: async (params) => {
        try {
            set({
                externalFiles: undefined,

                errorFetchExternalFiles: undefined,
                isLoadingFetchExternalFiles: true,
            })
            const data = await getExternalFiles(params)
            set({
                isLoadingFetchExternalFiles: false,
                externalFiles: data.objects,
            })
        } catch (error) {
            set({
                isLoadingFetchExternalFiles: false,
                errorFetchExternalFiles: 'Ошибка',
            })
        }
    },

    isLoadingDeleteRecord: false,
    errorDeleteRecord: undefined,
    deleteRecord: async (params) => {
        try {
            set({
                errorDeleteRecord: undefined,
                isLoadingDeleteRecord: true,
            })
            await deleteRecord(params)

            set({
                isLoadingDeleteRecord: false,
            })
        } catch (error) {
            set({
                errorDeleteRecord: 'Ошибка',
                isLoadingDeleteRecord: false,
            })
        }
    },

    isLoadingArchiveRecord: false,
    errorArchiveRecord: undefined,
    archiveRecord: async (params, data) => {
        try {
            set({
                errorArchiveRecord: undefined,
                isLoadingArchiveRecord: true,
            })
            await archiveRecord(params, data)

            set({
                isLoadingArchiveRecord: false,
            })
        } catch (error) {
            set({
                errorArchiveRecord: 'Ошибка',
                isLoadingArchiveRecord: false,
            })
        }
    },

    isLoadingDeleteWorkplace: false,
    errorDeleteWorkplace: undefined,
    deleteWorkplace: async (params) => {
        try {
            set({
                errorDeleteWorkplace: undefined,
                isLoadingDeleteWorkplace: true,
            })
            await deleteProject(params)

            set({
                workplaceDetail: undefined,
                isLoadingDeleteWorkplace: false,
            })
        } catch (error) {
            set({
                errorDeleteWorkplace: 'Ошибка',
                isLoadingDeleteWorkplace: false,
            })
        }
    },

    isLoadingArchiveWorkplace: false,
    errorArchiveWorkplace: undefined,
    archiveWorkplace: async (params, data) => {
        try {
            set({
                errorArchiveWorkplace: undefined,
                isLoadingArchiveWorkplace: true,
            })
            await archiveProject(params, data)

            set({
                errorFetchWorkplaceDetail: 'Ошибка',
                isLoadingArchiveWorkplace: false,
            })
        } catch (error) {
            set({
                errorArchiveWorkplace: 'Ошибка',
                isLoadingArchiveWorkplace: false,
            })
        }
    },
    idActiveEditRecord: undefined,
    setIdActiveEditRecord: (id?: string) => {
        set({
            idActiveEditRecord: id,
        })
    },
}))
